import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Shared shell for the public legal pages (/privacy, /terms). Same Deep
// Harbor header band as the front page, light reading body. Pages pass their
// sections as children; headings and paragraphs use the helpers below so the
// two documents read as one set.
export const CONTACT_EMAIL = 'info@simplicitydynamics.ca';
export const COMPANY_LEGAL_NAME = 'Simplicity Dynamics Inc.';

const Monogram = () => (
  <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-[#9ecbf7]/40 bg-[#9ecbf7]/10 text-sm font-bold tracking-wide text-[#9ecbf7]">
    SD
  </span>
);

export const H2 = ({ children }) => (
  <h2 className="mb-3 mt-10 text-xl font-semibold text-[#1d2b33] first:mt-0">{children}</h2>
);

export const P = ({ children }) => <p className="mb-4 leading-relaxed text-[#3f5468]">{children}</p>;

export const UL = ({ items }) => (
  <ul className="mb-4 list-disc space-y-1.5 pl-6 leading-relaxed text-[#3f5468]">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const LegalPage = ({ title, effective, children }) => {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} — Simplicity Dynamics`;
    window.scrollTo(0, 0);
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-[#f4f6f8] text-[#1d2b33]">
      <header className="bg-[#0f1620]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <Monogram />
            <span className="text-sm font-semibold tracking-wide text-white">Simplicity Dynamics</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-[#93a4b3] transition-colors hover:text-[#9ecbf7]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
        <div className="mx-auto max-w-3xl px-4 pb-10 pt-6 sm:px-6">
          <h1 className="text-3xl font-bold text-white sm:text-4xl">{title}</h1>
          <p className="mt-2 text-sm text-[#93a4b3]">Effective {effective}</p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <article className="rounded-2xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)] sm:p-10">
          {children}
        </article>
      </main>

      <footer className="border-t border-white/5 bg-[#0f1620] py-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-3 px-4 text-sm text-[#93a4b3] sm:px-6 md:flex-row">
          <span>&copy; 2026 {COMPANY_LEGAL_NAME} &middot; Calgary, Alberta, Canada</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="transition-colors hover:text-[#9ecbf7]">
              Privacy Policy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-[#9ecbf7]">
              Terms &amp; Conditions
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-[#9ecbf7]">
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
